/**
 * CustomizationManager - Manages item color selections and overlay rendering
 */
import * as THREE from "three"
import { API_URL } from "../config/env.js"

export class CustomizationManager {
  constructor(scene, panoramaLoader, tourId = null) {
    this.scene = scene
    this.panoramaLoader = panoramaLoader
    this.tourId = tourId
    this.overlayGroups = new Map() // Map<itemName, THREE.Group>
    this.activeSelections = new Map() // Map<itemName, colorName>
    this.availableItems = [] // Loaded from config
  }

  /**
   * Load available customization options for current panorama
   */
  async loadCustomizationOptions(panoId) {
    try {
      // Clear all existing overlays before loading new ones
      this.clearAll()

      // Build API URL with tourId if available
      const url = this.tourId
        ? `${API_URL}/virtual-tour/tours/${this.tourId}/overlays/${panoId}`
        : `${API_URL}/overlays/${panoId}`

      const response = await fetch(url)
      const data = await response.json()

      // Group by item name
      this.availableItems = this.groupOverlaysByItem(data)

      console.log(
        `✅ Loaded ${this.availableItems.length} customizable items for panorama ${panoId}`,
      )

      return this.availableItems
    } catch (error) {
      console.error("Failed to load customization options:", error)
      return []
    }
  }

  /**
   * Group overlays by item for UI rendering
   */
  groupOverlaysByItem(overlays) {
    const itemMap = new Map()

    overlays.forEach((overlay) => {
      if (!itemMap.has(overlay.item_name)) {
        itemMap.set(overlay.item_name, {
          name: overlay.item_name,
          label: overlay.item_label,
          colors: [],
        })
      }

      itemMap.get(overlay.item_name).colors.push({
        name: overlay.color_name,
        hex: overlay.color_hex,
        cubePathPrefix: overlay.cube_path_prefix,
      })
    })

    return Array.from(itemMap.values())
  }

  /**
   * Apply a color selection to an item
   */
  async selectColor(itemName, colorConfig, cameraPosition, panoRotY) {
    // Remove existing overlay for this item if any
    this.removeOverlay(itemName)

    // Load the overlay cube map
    const overlayTexture = await this.loadOverlayCubeMap(
      colorConfig.cubePathPrefix,
    )

    // Create overlay group
    const overlayGroup = this.createOverlayGroup(
      overlayTexture,
      cameraPosition,
      panoRotY,
    )

    // Store and add to scene
    this.overlayGroups.set(itemName, overlayGroup)
    this.activeSelections.set(itemName, colorConfig.name)
    this.scene.add(overlayGroup)

    console.log(`✓ Applied ${colorConfig.name} to ${itemName}`)
  }

  /**
   * Remove an overlay layer
   */
  removeOverlay(itemName) {
    const existingOverlay = this.overlayGroups.get(itemName)
    if (existingOverlay) {
      this.scene.remove(existingOverlay)
      this.disposeOverlayGroup(existingOverlay)
      this.overlayGroups.delete(itemName)
      this.activeSelections.delete(itemName)
    }
  }

  /**
   * Create an overlay panorama group (similar to base panorama but with transparency)
   */
  createOverlayGroup(texture, cameraPosition, panoRotY) {
    const group = new THREE.Group()

    // Use slightly larger geometry to render "on top" of base panorama
    const geometry = new THREE.BoxGeometry(501, 501, 501)
    const material = new THREE.ShaderMaterial({
      uniforms: {
        tCube: { value: texture },
        opacity: { value: 1.0 },
      },
      vertexShader: `
        varying vec3 vPosition;
        void main() {
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform samplerCube tCube;
        uniform float opacity;
        varying vec3 vPosition;
        void main() {
          vec3 direction = normalize(vPosition);
          direction.z = -direction.z;
          vec4 color = textureCube(tCube, direction);

          // Preserve alpha from PNG transparency
          gl_FragColor = vec4(color.rgb, color.a * opacity);
        }
      `,
      transparent: true,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.renderOrder = 0 // Render after base panorama (-1) but before hotspots (999+)
    group.add(mesh)

    // Apply same rotation as base panorama
    group.rotation.y = THREE.MathUtils.degToRad(panoRotY)

    // Position at camera location
    group.position.copy(cameraPosition)

    return group
  }

  /**
   * Load a cube map from overlay PNG files
   */
  async loadOverlayCubeMap(pathPrefix) {
    const loader = new THREE.CubeTextureLoader()

    return new Promise((resolve, reject) => {
      loader.load(
        [
          `/assets/panoramas/${pathPrefix}_r.png`,
          `/assets/panoramas/${pathPrefix}_l.png`,
          `/assets/panoramas/${pathPrefix}_u.png`,
          `/assets/panoramas/${pathPrefix}_d.png`,
          `/assets/panoramas/${pathPrefix}_f.png`,
          `/assets/panoramas/${pathPrefix}_b.png`,
        ],
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace
          resolve(texture)
        },
        undefined,
        (error) => {
          console.error("Error loading overlay texture:", error)
          reject(error)
        },
      )
    })
  }

  /**
   * Hide all overlays
   */
  hideAllOverlays() {
    this.overlayGroups.forEach((group) => {
      group.visible = false
    })
  }

  /**
   * Update overlay positions to follow camera (called in animation loop)
   */
  update(cameraPosition) {
    this.overlayGroups.forEach((overlayGroup) => {
      overlayGroup.position.copy(cameraPosition)
    })
  }

  /**
   * Clear all overlays (when changing panoramas)
   */
  clearAll() {
    this.overlayGroups.forEach((group, itemName) => {
      this.removeOverlay(itemName)
    })
  }

  /**
   * Get current selections for persistence
   */
  getSelections() {
    return Object.fromEntries(this.activeSelections)
  }

  /**
   * Restore selections (when returning to a panorama)
   */
  async restoreSelections(selections, cameraPosition, panoRotY) {
    for (const [itemName, colorName] of Object.entries(selections)) {
      const item = this.availableItems.find((i) => i.name === itemName)
      if (item) {
        const color = item.colors.find((c) => c.name === colorName)
        if (color) {
          await this.selectColor(itemName, color, cameraPosition, panoRotY)
        }
      }
    }
  }

  /**
   * Dispose overlay group and resources
   */
  disposeOverlayGroup(group) {
    if (!group) return

    group.traverse((child) => {
      if (child.geometry) child.geometry.dispose()
      if (child.material) {
        if (child.material.uniforms && child.material.uniforms.tCube) {
          child.material.uniforms.tCube.value.dispose()
        }
        child.material.dispose()
      }
    })
  }

  dispose() {
    this.clearAll()
  }
}
